import React, { Fragment, useContext } from "react";
import {
  Transition,
  TransitionChild,
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

import styles from "./Dialog.module.css";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import CloseIcon from "~/icons/CloseIcon";
import { LABELS } from "~/modules/checkout/constants";
import ThemeContext from "~/contexts/ThemeContext";
import { getLuma } from "~/helpers/colorHelpers";
import CircleCloseButton from "~/icons/CircleCloseIcon";

interface DialogProps {
  title?: string;
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Dialog: React.FC<DialogProps> = ({
  title,
  visible,
  onClose,
  children,
}) => {
  const { template } = useContext(ThemeContext);

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Header styles
  const headerBackgroundColor = template.navBar.backgroundColour;
  const fontColor = getLuma(headerBackgroundColor) < 150 ? "#fff" : "#000";

  return (
    <Transition show={visible} as={Fragment}>
      <HeadlessDialog
        as="div"
        className={styles.dialog}
        open={visible}
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={styles.backdrop} aria-hidden="true" />
        </TransitionChild>

        <div className={styles.container}>
          <div className={isMobile ? styles.panel : styles.modalContainer}>
            <TransitionChild
              as={Fragment}
              enter="transform transition duration-500"
              enterFrom={isMobile ? "translate-y-full" : "scale-95 opacity-0"}
              enterTo={isMobile ? "translate-y-0" : "scale-100 opacity-100"}
              leave="transform transition duration-500"
              leaveFrom={isMobile ? "translate-y-0" : "scale-100 opacity-100"}
              leaveTo={isMobile ? "translate-y-full" : "scale-95 opacity-0"}
            >
              <DialogPanel className={isMobile ? styles.drawer : styles.modal}>
                {!!title && (
                  <>
                    <div
                      className={styles.mobileHeader}
                      style={{
                        backgroundColor: headerBackgroundColor,
                        color: fontColor,
                      }}
                    >
                      <DialogTitle as="h2">{title}</DialogTitle>
                      <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={LABELS.CLOSE_DRAWER}
                      >
                        <CloseIcon color={fontColor} />
                      </button>
                    </div>
                    <div className={styles.desktopHeader}>
                      <DialogTitle as="span">{title}</DialogTitle>
                      <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={LABELS.CLOSE_DRAWER}
                      >
                        <CircleCloseButton color="#828282" />
                      </button>
                    </div>
                  </>
                )}
                <div className={styles.content}>{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HeadlessDialog>
    </Transition>
  );
};

export default Dialog;
